/**
 * `JobPositioningView` — componente presentazionale del posizionamento su una
 * singola professione (Job e-CF 3.0). Riceve un `JobMatch` già caricato e non
 * esegue alcun fetch: la scelta della fonte dati, del path di ritorno e del
 * path di dettaglio competenza è responsabilità del chiamante.
 *
 * Rende esattamente ciò che `JobDetail` mostra per un job caricato: la
 * `PageHeader` (titolo "Dettaglio professione", sottotitolo e azione "indietro"
 * dai prop), la card‑intestazione (hero) con il donut della Copertura, il nome,
 * sintesi/mission/KPI, la barra della Proficiency e il footer; le card
 * informative secondarie (Deliverable/Compiti) e la tabella delle competenze
 * richieste (Peso/Livello/Score/Analisi Gap).
 *
 * È riusato sia dalla dashboard personale/Team (`JobDetail`) sia dal dettaglio
 * di posizionamento dei profili CV esterni (`ExternalCvJobDetail`).
 */
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  MAX_SKILL_WEIGHT,
  type JobCompetenceMatch,
  type JobMatch,
} from '../shared/dto';
import { scoreColor } from './StarRating';
import { LivelloBadge } from './LivelloBadge';
import { LivelloGuide } from './LivelloGuide';
import { LevelBar } from './LevelBar';
import { GapAnalysisGuide } from './GapAnalysisGuide';
import { ScoreBadge } from './ScoreBadge';
import { ScoreGuide } from './ScoreGuide';
import {
  Button,
  Card,
  DataTable,
  DotRating,
  Icon,
  PageHeader,
  type Column,
  type SortDirection,
} from './ui';

/** Etichetta descrittiva del peso, allineata a `SkillWeightEditor`. */
const WEIGHT_HINT: Record<number, string> = {
  1: 'Marginale',
  2: 'Bassa',
  3: 'Media',
  4: 'Alta',
  5: 'Essenziale',
};

/** Formatta una percentuale `0..100` come intero con `%`, o `—` se `null`. */
function formatPercent(value: number | null): string {
  if (value === null) return '—';
  return `${Math.round(Math.max(0, value))}%`;
}

/** Scompone un testo separato da virgole in voci non vuote (per gli elenchi). */
function splitItems(text: string): string[] {
  return text
    .split(',')
    .map((item) => item.trim())
    .filter((item) => item !== '');
}

const DONUT_RADIUS = 16;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;

/** Anello di progresso di una percentuale `0..100` (Copertura del Job). */
function ScoreDonut({ score, label }: { score: number; label: string }) {
  const clamped = Math.min(100, Math.max(0, score));
  const rounded = Math.round(clamped);
  const ringColor = scoreColor(clamped / 10) ?? 'var(--color-primary)';
  const dash = (clamped / 100) * DONUT_CIRCUMFERENCE;

  return (
    <div
      className="competency-match-card__donut"
      role="img"
      aria-label={`${label} ${rounded} su 100`}
      title={`${label} ${rounded}/100`}
    >
      <svg viewBox="0 0 36 36" className="competency-match-card__donut-svg" aria-hidden="true">
        <circle className="competency-match-card__donut-track" cx="18" cy="18" r={DONUT_RADIUS} />
        <circle
          className="competency-match-card__donut-fill"
          cx="18"
          cy="18"
          r={DONUT_RADIUS}
          stroke={ringColor}
          strokeDasharray={`${dash} ${DONUT_CIRCUMFERENCE - dash}`}
          transform="rotate(-90 18 18)"
        />
      </svg>
      <span className="competency-match-card__donut-value" aria-hidden="true">
        {rounded}
      </span>
    </div>
  );
}

/**
 * `MetricBar` — barra di progresso etichettata per una percentuale `0..100`,
 * identica a quella della `CompetencyMatchCard`. `null` → trattino "—".
 */
function MetricBar({ label, value }: { label: string; value: number | null }) {
  if (value === null) {
    return (
      <div className="competency-match-card__metric">
        <dt>{label}</dt>
        <dd>
          <span
            className="competency-match-card__value competency-match-card__value--empty"
            aria-label={`${label} non disponibile`}
          >
            —
          </span>
        </dd>
      </div>
    );
  }

  const clamped = Math.min(100, Math.max(0, value));
  const exact = Math.round(Math.max(0, value));
  const color = scoreColor(clamped / 10);

  return (
    <div className="competency-match-card__metric">
      <dt>{label}</dt>
      <dd>
        <span
          role="progressbar"
          aria-valuenow={exact}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`${label} ${formatPercent(clamped)}`}
          className="competency-match-card__bar"
        >
          <span
            className="competency-match-card__bar-fill"
            style={{
              width: `${clamped}%`,
              ...(color ? { background: color } : {}),
            }}
          />
        </span>
        <span className="competency-match-card__value">{formatPercent(clamped)}</span>
      </dd>
    </div>
  );
}

export interface JobPositioningViewProps {
  /** Il posizionamento sulla professione, già caricato dal chiamante. */
  job: JobMatch;
  /** Azione "indietro": etichetta e callback (contesto specifico del chiamante). */
  backTo: { label: string; onClick: () => void };
  /** Costruisce il path del dettaglio competenza per l'id indicato. */
  competenceDetailPath: (competenceId: string) => string;
  /**
   * Percorso di provenienza propagato come `state.from` sui link alle
   * competenze, così la pagina di dettaglio competenza può tornare a questa
   * professione invece che al suo default ("Il mio CV"). Assente ⇒ nessuno
   * stato di navigazione.
   */
  competenceBackTo?: string;
  /** Sottotitolo della `PageHeader` (varia con il contesto). */
  subtitle: string;
}

export function JobPositioningView({
  job,
  backTo,
  competenceDetailPath,
  competenceBackTo,
  subtitle,
}: JobPositioningViewProps) {
  const columns = useMemo<Column<JobCompetenceMatch>[]>(
    () => [
      {
        key: 'name',
        header: 'Competenza',
        sortable: true,
        sortComparator: (a, b, direction: SortDirection) => {
          const sign = direction === 'desc' ? -1 : 1;
          return sign * a.name.localeCompare(b.name, 'it');
        },
        className: 'job-detail__col-name',
        render: (c) => (
          <Link
            to={competenceDetailPath(c.competenceId)}
            state={competenceBackTo !== undefined ? { from: competenceBackTo } : undefined}
            className="coverage-name__link"
            aria-label={`Vai all'assessment della competenza ${c.name}`}
          >
            {c.name}
            <Icon name="external" size={14} className="coverage-name__link-icon" />
          </Link>
        ),
      },
      {
        key: 'weight',
        header: 'Peso',
        sortable: true,
        sortComparator: (a, b, direction: SortDirection) => {
          const sign = direction === 'desc' ? -1 : 1;
          return sign * (a.weight - b.weight);
        },
        render: (c) => (
          <DotRating
            value={c.weight}
            onChange={() => {}}
            readOnly
            size="sm"
            max={MAX_SKILL_WEIGHT}
            label={`Peso di ${c.name}`}
            optionLabel={(n) => `${n} — ${WEIGHT_HINT[n] ?? ''}`.trim()}
          />
        ),
        className: 'skill-list__score-col',
      },
      {
        key: 'requiredLevel',
        header: (
          <span className="score-calc-guide__header">
            Livello
            <LivelloGuide className="score-calc-guide__trigger" />
          </span>
        ),
        render: (c) => (
          <LivelloBadge livello={c.requiredLevel} label="Livello richiesto" />
        ),
      },
      {
        key: 'userScore10',
        header: (
          <span className="score-calc-guide__header">
            Score
            <ScoreGuide className="score-calc-guide__trigger" />
          </span>
        ),
        sortable: true,
        sortComparator: (a, b, direction: SortDirection) => {
          const sign = direction === 'desc' ? -1 : 1;
          return sign * ((a.userScore10 ?? -1) - (b.userScore10 ?? -1));
        },
        render: (c) =>
          c.userScore10 === null ? (
            <span className="job-detail__muted">—</span>
          ) : (
            <ScoreBadge value={c.userScore10} label="Score" />
          ),
        className: 'skill-list__score-col',
      },
      {
        key: 'status',
        header: (
          <span className="score-calc-guide__header">
            Analisi Gap
            <GapAnalysisGuide className="score-calc-guide__trigger" />
          </span>
        ),
        render: (c) => (
          <LevelBar
            owned={c.ownedLevel}
            required={c.requiredLevel}
            available={c.availableLevels}
          />
        ),
      },
    ],
    // `competenceDetailPath` dipende dal contesto del chiamante: ricostruisce le
    // colonne quando cambia il path del dettaglio competenza (o la provenienza).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [competenceDetailPath, competenceBackTo],
  );

  // Blocchi informativi secondari (sotto la card principale), mostrati solo se
  // valorizzati. Sintesi, Mission e KPI vivono nella card "hero" principale;
  // qui restano Deliverable e Compiti, che sono valori separati da virgola nel
  // CSV: si mostrano come elenco puntato.
  const infoBlocks: { title: string; text: string; list?: boolean }[] = [
    ...(job.deliverables
      ? [{ title: 'Deliverable', text: job.deliverables, list: true }]
      : []),
    ...(job.tasks ? [{ title: 'Compiti', text: job.tasks, list: true }] : []),
  ];

  return (
    <section className="competence-detail job-detail" aria-labelledby="job-detail-title">
      <PageHeader
        title="Dettaglio professione"
        titleId="job-detail-title"
        subtitle={subtitle}
        actions={
          <div className="competence-detail__header-actions">
            <Button type="button" variant="ghost" onClick={backTo.onClick}>
              {backTo.label}
            </Button>
          </div>
        }
      />

      {/* Card principale della professione, resa come la "competenza principale"
          (hero) della Dashboard: badge, donut della Copertura, nome grande,
          sintesi e mission, la barra della Proficiency e il footer. Qui
          l'accento sul bordo sinistro è mantenuto come nella hero. */}
      <article className="competency-match-card competency-match-card--hero">
        <ScoreDonut score={job.coveragePercent} label="Copertura" />

        <div className="competency-match-card__header">
          <span className="competency-match-card__badge">Professione</span>
          <h2 className="competency-match-card__name">{job.name}</h2>
        </div>
        {job.summary && (
          <p className="competency-match-card__description">{job.summary}</p>
        )}
        {job.mission && (
          <p className="competency-match-card__mission">
            <span className="competency-match-card__mission-label">Mission</span>
            {job.mission}
          </p>
        )}
        {job.kpi && (
          <p className="competency-match-card__mission">
            <span className="competency-match-card__mission-label">KPI</span>
            {job.kpi}
          </p>
        )}
        <dl className="competency-match-card__metrics">
          <MetricBar label="Proficiency" value={job.proficiencyPercent} />
        </dl>
        <div className="competency-match-card__footer">
          <span
            className="competency-match-card__skills"
            aria-label={`${job.coveredCompetenceCount} di ${job.totalCompetenceCount} competenze coperte`}
          >
            {job.coveredCompetenceCount}/{job.totalCompetenceCount} competenze coperte
          </span>
          <span
            className="competency-match-card__chip"
            aria-label={`Match ${formatPercent(job.matchPercent)}`}
          >
            {formatPercent(job.matchPercent)} match
          </span>
        </div>
      </article>

      {/* Card informative secondarie: deliverable, compiti e KPI.
          Deliverable e Compiti sono elenchi puntati (voci separate da virgola). */}
      {infoBlocks.length > 0 && (
        <div className="competence-detail__info">
          {infoBlocks.map((block) => (
            <Card key={block.title} className="competence-detail__info-card">
              <h3 className="competence-detail__info-title">{block.title}</h3>
              {block.list ? (
                <ul className="job-detail__list">
                  {splitItems(block.text).map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              ) : (
                <p className="competence-detail__info-text">{block.text}</p>
              )}
            </Card>
          ))}
        </div>
      )}

      <DataTable
        rows={job.competences}
        columns={columns}
        rowKey={(c) => c.competenceId}
        emptyMessage="Questa professione non ha competenze richieste configurate."
        defaultSort={{ key: 'weight', direction: 'desc' }}
        paginated={false}
      />
    </section>
  );
}
